/**
 * The plans this browser has been told are gone.
 *
 * Everything else about an exercise plan is the backend's now — listing,
 * reading, creating, rewriting and sending all live in
 * `exercisePlansService`, and a create or a save answers with the plan, so
 * there is nothing left for the browser to hold a copy of.
 *
 * Except deleting. There is no `DELETE` endpoint, so the server keeps
 * returning a plan the dietitian has just been told was removed — and a list
 * that redrew it would make a liar of the toast. So the panel drops these from
 * what it fetched, and this is how it knows which ones those are.
 *
 * It lasts as long as the tab does. A reload brings the plan back, which the
 * confirm says plainly rather than leaving anyone to discover it.
 */

const deletedIds = new Set<string>();

/**
 * Rebuilt on every change rather than derived per call, so the snapshot keeps
 * its identity between them — `useSyncExternalStore` compares snapshots by
 * reference, and a fresh array each time is an infinite re-render.
 */
let deletedPlanIds: string[] = [];

const listeners = new Set<() => void>();

const publish = () => {
  deletedPlanIds = Array.from(deletedIds);
  listeners.forEach((listener) => listener());
};

/**
 * Subscribes to every change, and returns the unsubscribe.
 * `useSyncExternalStore` is what calls this — see `useDeletedExercisePlanIds`.
 */
export const subscribeToExercisePlans = (
  listener: () => void
): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getDeletedExercisePlanIds = (): string[] => deletedPlanIds;

export const hideExercisePlan = (planId: string): void => {
  deletedIds.add(planId);
  publish();
};

/**
 * Forgets them all.
 *
 * Test-only. It is a module singleton, so without this a plan one test hides
 * is still missing from the list the next one reads. Nothing in the app calls
 * it: a reload is what forgets them there.
 */
export const resetExercisePlansStore = (): void => {
  deletedIds.clear();
  publish();
};
